import React, { useState } from 'react';

import { updateCar } from '../../../api/carsApi';
import { formatPhoneBr, phoneDigits } from '../../../utils/phoneMask';
import { notify } from '../../../shared/feedback/notify';
import './OwnerContactModal.css';
import '../../../shared/ui/modal-close-button.css'; // X padrão do sistema

interface OwnerContactModalProps {
  isOpen: boolean;
  carId: string;
  plate: string;
  ownerNameAtual?: string | null;
  ownerPhoneAtual?: string | null;
  onCancel: () => void;
  /** Chamado após gravar, com os valores salvos — quem abriu decide o que fazer em seguida. */
  onSaved: (dados: { ownerName: string; ownerPhone: string }) => void;
}

/**
 * Cadastro rápido do contato do proprietário, a partir de um atendimento já
 * ENTREGUE.
 *
 * Existe porque o modal de ações do atendimento encerrado é somente leitura
 * (`deliveredMode`) — não há caminho de edição ali —, e o telefone é a chave de
 * acesso do link público: sem ele a pesquisa de satisfação não pode ser enviada.
 * Antes, o usuário via o erro e ficava sem saída na própria tela.
 *
 * O `PATCH /cars/:id` aceita atendimento encerrado por decisão (07/08/2026):
 * observação e dados de cadastro seguem editáveis depois da ENTREGA.
 */
const OwnerContactModal: React.FC<OwnerContactModalProps> = ({
  isOpen,
  carId,
  plate,
  ownerNameAtual,
  ownerPhoneAtual,
  onCancel,
  onSaved,
}) => {
  const [nome, setNome] = useState(ownerNameAtual || '');
  const [telefone, setTelefone] = useState(formatPhoneBr(ownerPhoneAtual || ''));
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSalvar = async () => {
    const digitos = phoneDigits(telefone);
    // 10 a 13 dígitos é a faixa que o backend aceita e a que o link público
    // consegue verificar depois.
    if (digitos.length < 10 || digitos.length > 13) {
      setErro('Informe o celular completo, com DDD.');
      return;
    }

    setSalvando(true);
    setErro(null);
    try {
      await updateCar(carId, { ownerName: nome.trim() || undefined, ownerPhone: digitos } as any);
      onSaved({ ownerName: nome.trim(), ownerPhone: digitos });
    } catch (error) {
      // Sem isto o modal fecharia como se tivesse salvado, e o envio seguinte
      // falharia de novo pelo mesmo motivo.
      setErro(null);
      notify.actionError(error, { action: 'update', subject: 'o contato do proprietário' });
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="owner-contact-overlay" onClick={onCancel}>
      <div className="owner-contact-modal" onClick={(e) => e.stopPropagation()}>
        <div className="owner-contact-header">
          <h3>Contato do proprietário</h3>
          <button type="button" className="close-button" onClick={onCancel} aria-label="Fechar">
            &times;
          </button>
        </div>

        <p className="owner-contact-hint">
          O veículo <strong>{plate}</strong> ainda não tem o contato do proprietário. O celular é o que
          o associado usa para abrir a pesquisa — sem ele o link não pode ser enviado.
        </p>

        <div className="owner-contact-field">
          <label htmlFor="owner-contact-nome">Nome do proprietário</label>
          <input
            id="owner-contact-nome"
            type="text"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            placeholder="Como ele será chamado na mensagem"
          />
        </div>

        <div className="owner-contact-field">
          <label htmlFor="owner-contact-telefone">Celular</label>
          <input
            id="owner-contact-telefone"
            type="tel"
            value={telefone}
            onChange={(e) => setTelefone(formatPhoneBr(e.target.value))}
            placeholder="(21) 99999-8888"
            autoFocus
          />
        </div>

        {erro && <div className="owner-contact-erro">{erro}</div>}

        <div className="owner-contact-actions">
          <button type="button" className="owner-contact-btn" onClick={onCancel} disabled={salvando}>
            Cancelar
          </button>
          <button
            type="button"
            className="owner-contact-btn primary"
            onClick={handleSalvar}
            disabled={salvando}
          >
            {salvando ? 'Salvando...' : 'Salvar e enviar pesquisa'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OwnerContactModal;
